import {
  ArrowsClockwiseIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  SlidersHorizontalIcon,
  StarFourIcon,
} from '@phosphor-icons/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BreadcrumbSwitcher } from './BreadcrumbSwitcher';
import { ProjectHubToggleButton } from './ProjectHubToggleButton';
import { RouteBreadcrumb } from './RouteBreadcrumb';

import { ComingSoonDialog } from '@/components/discover/ComingSoonDialog';
import { NewChatButton } from '@/components/layout/NewChatButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageHeaderActionLayout } from '@/components/layout/PageHeaderActionLayout';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Logo, LogoText } from '@/components/ui/logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCurrentContext } from '@/hooks/useCurrentContext';
import { useHeader } from '@/hooks/useHeader';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { useCanSplitRightSlot } from '@/hooks/useRightSlotBreakpoints';
import { useSubscription } from '@/hooks/useSubscription';
import { parseInsightsChannel } from '@/lib/analytics/insights-channels';
import { MENU_ITEM_MOBILE } from '@/lib/ui-tokens';
import { useArtifactStore } from '@/stores/useArtifactStore';
import { useProjectHubStore } from '@/stores/useProjectHubStore';

/**
 * The five first-class objects that name themselves in the header, each with
 * the SAME mark its sidebar row carries. That repetition is the point: the
 * glyph confirms where you landed without being read.
 *
 * The description is rendered as the title's `title` attribute (a native
 * tooltip), not as visible copy — it stays available for the nouns that need
 * defining without costing vertical space on every visit.
 */
const OBJECT_PAGE_HEADERS: Record<string, { label: string; description: string }> = {
  '/briefs': {
    label: 'Briefs',
    description:
      'One batch of work — the goal, the audiences you are speaking to, and the concepts to make for each.',
  },
  '/studio': {
    label: 'Studio',
    description:
      'Where images and video get made — start from a prompt, a product, a saved Look, or a reference.',
  },
  // Assets is deliberately NOT here (removed 2026-08-15). Opening an asset
  // renders its own inline breadcrumb ("Assets › <title>", no tile — see
  // `assetsBreadcrumb()` in `AssetsPage.tsx`), so the grid page carried a
  // tile its own detail view never had — one section disagreeing with
  // itself. Founder: the Assets header "shouldn't carry that icon glyph —
  // it should read as a normal breadcrumb." Falls through to the plain
  // `title: 'Assets'` branch below instead, matching Insights/Discover's
  // own no-tile, no-ancestor treatment. Studio, Briefs, and Campaigns keep
  // their tile here on purpose — this is scoped to the one page that was
  // asked for and the one inconsistency that was found; see the `/assets`
  // branch below for the Studio/Workflows recommendation.
  '/campaigns': {
    label: 'Campaigns',
    description:
      'Paid campaigns across Meta, Google and LinkedIn — spend, delivery, and what each one returned.',
  },
  '/workflows': {
    label: 'Workflows',
    description:
      'Saved runs you can start on demand — each one asks for what it needs, then does the work end to end.',
  },
  '/agents': {
    label: 'Workflows',
    description:
      'Saved runs you can start on demand — each one asks for what it needs, then does the work end to end.',
  },
};

/**
 * PageHeaderManager - Manages PageHeader display above error boundaries
 * Shows headers immediately while content loads progressively
 */
export const UpgradeHeaderAction: React.FC<{ onUpgrade: () => void }> = ({ onUpgrade }) => {
  const { data: subscription } = useSubscription();
  if (subscription?.plan === 'pro_max') return null;
  return (
    <PageHeaderActionLayout
      desktopPrimary={
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="flat"
              size="sm"
              aria-label="Upgrade your plan"
              className="header-upgrade-pill h-nav-icon-mobile md:h-nav-icon-desktop px-2.5 text-mode-active-foreground hover:bg-transparent hover:text-mode-active-foreground md:px-3"
              onClick={onUpgrade}
            >
              <StarFourIcon className="size-4 md:size-3.5" weight="fill" />
              <span className="hidden sm:inline">Upgrade</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Upgrade your plan</TooltipContent>
        </Tooltip>
      }
      mobilePrimary={
        <CircleIconButton
          size="header"
          ariaLabel="Upgrade your plan"
          tooltip="Upgrade your plan"
          onClick={onUpgrade}
        >
          <StarFourIcon weight="fill" />
        </CircleIconButton>
      }
    />
  );
};

export const PageHeaderManager: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isSignedOut = !user;
  // Billing belongs to the account owner; a teammate cannot upgrade it.
  const isTeammate = useCurrentContext().role === 'member';
  // PageHeaderManager is the canonical whole-store consumer — it renders
  // whatever the current page pushed via setTitle/setActions/setBottom/etc.
  // Safe because it does NOT call any setters (read-only consumer). Every
  // other file destructuring a zustand store is the bug pattern.
  // eslint-disable-next-line no-restricted-syntax
  const {
    title: customTitle,
    actions: customActions,
    bottomComponent,
    backHref,
    backLabel,
  } = useHeader();

  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  useEffect(() => {
    const handler = () => setComingSoonOpen(true);
    window.addEventListener('openSignupDialog', handler);
    return () => window.removeEventListener('openSignupDialog', handler);
  }, []);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Home composer breadcrumb data — hooks must be called unconditionally
  const isDrawerOpen = useArtifactStore((s) => s.isDrawerOpen);
  // UF-7: when a running workflow's tracker owns the right slot it renders its
  // OWN collapse glyph — so the app-bar project-hub toggle (same glyph) becomes
  // a confusing duplicate. Track the workflow-drawer case to hide the app-bar one.
  const drawerBaseMode = useArtifactStore((s) => s.baseMode);
  // PH-1 (ARCH-3): planId resolution at layout level. Source priority:
  // URL param ?planId=X (set by CreateChatPage routing) > artifact-store
  // planContext (set when chat resolves a plan from a session). The URL
  // wins because it's available BEFORE the chat session resolves; using
  // only planContext caused the toggle button to flicker on first paint.
  const planContextId = useArtifactStore((s) => s.planContext?.planId ?? null);
  const planIdFromUrl = searchParams.get('planId');
  const planId = planIdFromUrl || planContextId;
  const isProjectBound = planId !== null;
  const isHubOpen = useProjectHubStore((s) => s.isOpen);
  const canShowProjectRail = useCanSplitRightSlot();
  const hasArtifacts = useArtifactStore((s) =>
    s.agentMessages.some((m) =>
      m.parts?.some((p) => {
        const type = (p as { type?: string }).type;
        const state = (p as { state?: string }).state;
        return (
          typeof type === 'string' &&
          type.startsWith('tool-generate_') &&
          state === 'output-available'
        );
      }),
    ),
  );
  const { projectId: pathProjectId } = useParams();
  const projectIdFromUrl = pathProjectId || searchParams.get('collectionId') || undefined;
  const activeTab = searchParams.get('tab') || 'summary';

  // Insights header's "Email report" action (see /analytics branch below)
  // needs Shopify-connection state at THIS layout level now that the button
  // lives here instead of in InsightsPage. Hooks must be called
  // unconditionally at the component top level — never inside pageConfig's
  // useMemo/switch below — so this is hoisted here like every other
  // route-agnostic hook above, even though the result is only consumed on
  // one route. TanStack Query dedupes the underlying fetch/channel against
  // any other `usePlatformIntegrations()` caller (e.g. InsightsPage itself,
  // Settings) via the shared `['platform-integrations', ownerId]` key.
  const { hasShopifyConnected } = usePlatformIntegrations();

  // Define page configurations - MUST NOT RETURN NULL HERE
  const pageConfig = useMemo(() => {
    const path = location.pathname;

    // An object page names itself in the CHROME BAR, with its own mark.
    //
    // This replaces `PageIdentity`, which put the same glyph + title + a
    // one-line description at the top of the page BODY. That was closer to the
    // reference product, but in this app it pushed every list down by ~64px and
    // read as a landing-page band above the real content. Founder, 2026-08-12:
    // move it back up to the header, keep the icon, drop the sentence.
    //
    // The description survives as the title's tooltip rather than being thrown
    // away — it is still the only place several of these nouns are defined.
    //
    // Exact paths only. `/briefs/:id` and the campaign authoring routes are
    // detail surfaces and keep their own breadcrumb/title.
    const objectPage = OBJECT_PAGE_HEADERS[path];
    if (objectPage) {
      return {
        // NO ObjectGlyph here, deliberately. The tile is a WAYFINDING device,
        // and wayfinding only pays where you are choosing among options: in
        // the sidebar you scan four rows and pick one, and in the collapsed
        // icon-only rail the mark is the ONLY affordance there is. By the time
        // you are reading a page header you have already arrived — nothing is
        // being chosen, and the glyph just restates the title beside it in a
        // louder register. Founder, 2026-08-24, on seeing it live: it "looks
        // weird on the pageheader manager". On /briefs it was a small dense
        // chip sitting directly above the page's own full-width gradient
        // banner, competing with it and losing.
        //
        // `RouteBreadcrumb` drops its matching root glyph for the same reason
        // and in the same change — the two were deliberately kept in sync so
        // the icon does not appear and disappear as you navigate in and out of
        // an object. They still are; the shared value is just "no glyph" now.
        title: (
          <span className="flex min-w-0 items-center gap-2" title={objectPage.description}>
            <span className="truncate">{objectPage.label}</span>
          </span>
        ),
        className: '',
        actions: customActions,
      };
    }

    if (path === '/') {
      // NO title on the home chat, at any breakpoint.
      //
      // It carried "Create › New conversation ⌄": a root whose href was
      // `/?new=true` (a reset dressed as navigation, and the fifth entry point
      // for a new chat) over a leaf naming a conversation that does not exist
      // on the empty state.
      //
      // Substituting the session's name was tried and is worse: at any real
      // length it dominates the bar, and the conversation is already on screen —
      // the messages ARE the context, and the sidebar already lists sessions by
      // name. Gemini and ChatGPT both show no title on their chat home for the
      // same reason. Claude Code shows one only because you arrive there from a
      // session list; `/` is the default landing, not a detail view.
      //
      // The bar is therefore ☰ + the one action. Nothing left to remove.
      // PH-1 design-system audit pass 2 (2026-05-08): the toggle ONLY
      // means "project hub" now. Previously it doubled as the drawer
      // toggle for non-project chats — same icon, different surface,
      // confusing. The artifact drawer has its own internal close
      // affordance (DrawerCloseAffordance, X icon top-right of drawer)
      // and re-opens when users click an artifact chip in chat. The
      // header toggle is no longer needed for drawer.
      // Result: button has ONE meaning. Project bound → hub toggle.
      // Otherwise → button hidden.
      // UF-7: also hide it while a workflow's artifact-drawer owns the right slot
      // — the drawer has its own collapse glyph, so two identical glyphs read as
      // a confusing duplicate dismiss affordance (the user's #1 chrome complaint).
      const showToggle =
        isProjectBound && canShowProjectRail && !(isDrawerOpen && drawerBaseMode === 'workflow');
      return {
        title: null,
        className: '',
        trailingActions: showToggle ? (
          <ProjectHubToggleButton isHubOpen={isHubOpen} planId={planId} />
        ) : null,
      };
    }

    if (path === '/create' || path.startsWith('/create/')) {
      return { skipRender: true };
    }

    // Content ideas uses the app-standard page header for consistency; the board
    // body renders the 3-column workspace below it (chrome={false}).
    if (path === '/board') {
      // Discover / Content ideas — see the idea-surface branch in
      // RouteBreadcrumb for why /board names Discover as its root.
      return { title: <RouteBreadcrumb />, className: '' };
    }

    if (path.startsWith('/insights')) {
      // Platform chip rail lives IN the page body now (mirrors Discover) so it
      // shows on both mobile + desktop and reads alongside an "Updated · {tab}"
      // indicator. Keeping it in the header was desktop-only and orphaned the
      // mobile filter UX.
      return {
        // A PLAIN STRING, deliberately. This was
        // `<span className="text-sm font-medium tracking-tight text-foreground/80">`,
        // which is the DESKTOP half of the h1's responsive title scale written
        // as a constant — so on mobile Insights rendered its title at 15px/500
        // while /calendar and /discover rendered theirs at 16px/600 (measured
        // on prod, 285px viewport, 2026-07-25). The h1 already applies exactly
        // these classes at `md:`; restating them only cancelled mobile.
        //
        // No nested breadcrumb here, unlike /library or /projects: the leaf
        // would be the current tab (`?view=content` → "Content"), and the tab
        // strip naming it is on screen immediately beneath this header at every
        // breakpoint. A crumb that repeats the selected tab two rows above it
        // is chrome, not orientation.
        title: 'Insights',
        className: '',

        // Page-level filter + overflow controls stay in one visible group at
        // every breakpoint. Right-side workbench headers intentionally retain
        // the mobile-only `header-action-pill` treatment.
        // ⚠️ Nesting order is load-bearing. This was
        // `DropdownMenuTrigger asChild > Tooltip > TooltipTrigger asChild >
        // button`, which made the kebab a SILENT NO-OP — clicking it did
        // nothing at all (founder, 2026-07-24). `Tooltip` is `Tooltip.Root`, a
        // context provider that renders no DOM and forwards no props, so
        // `asChild` handed the trigger's onPointerDown/ref/aria to a component
        // that dropped them on the floor. The menu therefore never received a
        // click and never opened. Both triggers must resolve onto the SAME real
        // element: TooltipTrigger asChild → DropdownMenuTrigger asChild →
        // button.
        //
        // No filter control here any more. The window and the channel now live
        // in the SCOPE BAR at the top of the page content
        // (`AnalyticsProvenanceLine` → `ScopeBar`), labelled with their current
        // values. Keeping a second, anonymous funnel up here would be two
        // controls for one setting — and the header one was the version that
        // could not say what it was set to.
        // ⚠️ RATCHETED VIOLATION, and the guard names it: this `actions:` JSX
        // still lives in the manager instead of `InsightsPage.tsx` calling
        // `setHeaderActions` like the other 11 routes. It is not moved here
        // because `src/pages/analytics/*` is owned by a concurrent lane. What
        // IS fixed is the chrome: the group now comes from
        // `PageHeaderActionLayout`, so the ground is supplied by the ONE layout
        // component rather than a hand-rolled `.header-nav-btn` className.
        actions: (
          <PageHeaderActionLayout
            desktopActions={
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <CircleIconButton
                        ariaLabel="More actions"
                        size="header"
                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                      >
                        <DotsThreeIcon weight="bold" />
                      </CircleIconButton>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>More actions</TooltipContent>
                </Tooltip>
                {/* ⚠️ These three items had drifted from the canonical app menu row
                  on four counts at once, which is exactly the "does this match the
                  rest of the app?" the founder asked about (2026-07-24). Measured
                  against `ChatSessionMenu` and `ArticleHeaderActions`, the two
                  surfaces that define the pattern:
                    · icons were `size-4`, canonical is `size-3.5`
                    · icons were full-strength foreground, canonical quiets them to
                      `text-muted-foreground/70` so the LABEL leads the row and the
                      icon stays a scanning anchor
                    · labels were bare text nodes, canonical wraps them in `<span>`
                      (which is what keeps `gap` measuring label-to-icon rather
                      than to a stray text node)
                    · `cn(MENU_ITEM_MOBILE, 'gap-2')` re-specified the gap the
                      token already sets, so any future change to the token would
                      silently not reach this menu — the mechanism by which drift
                      becomes permanent
                  Also `onSelect`, not `onClick`: Radix closes the menu and returns
                  focus off its own select event, and it is what every sibling
                  uses. */}
                <DropdownMenuContent align="end" className="w-48">
                  {hasShopifyConnected && (
                    <DropdownMenuItem
                      className={MENU_ITEM_MOBILE}
                      onSelect={() => {
                        window.dispatchEvent(
                          new CustomEvent('analyticsEmailReport', {
                            detail: { page: 'analytics' },
                          }),
                        );
                      }}
                    >
                      <EnvelopeSimpleIcon
                        className="size-3.5 text-muted-foreground/70"
                        weight="regular"
                      />
                      <span>Email report</span>
                    </DropdownMenuItem>
                  )}
                  {parseInsightsChannel(searchParams.get('channel')) === 'all' && (
                    <DropdownMenuItem
                      className={MENU_ITEM_MOBILE}
                      onSelect={() => {
                        window.dispatchEvent(
                          new CustomEvent('analyticsExport', { detail: { page: 'analytics' } }),
                        );
                      }}
                    >
                      <DownloadSimpleIcon
                        className="size-3.5 text-muted-foreground/70"
                        weight="regular"
                      />
                      <span className="flex flex-col">
                        <span>Export CSV</span>
                        <span className="text-detail text-muted-foreground">
                          Daily social metrics
                        </span>
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className={MENU_ITEM_MOBILE}
                    onSelect={() => {
                      window.dispatchEvent(
                        new CustomEvent('analyticsRefresh', { detail: { page: 'analytics' } }),
                      );
                    }}
                  >
                    <ArrowsClockwiseIcon
                      className="size-3.5 text-muted-foreground/70"
                      weight="regular"
                    />
                    <span>Refresh data</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        ),
        showShimmer: true,
      };
    }

    // `/publishing` is the live prefix; `/calendar` is kept because
    // `PublishingLegacyRedirect` renders one frame at the old path before it
    // swaps, and a header that blanks for that frame is a visible flash.
    if (path.startsWith('/publishing') || path.startsWith('/calendar')) {
      return {
        title: customTitle || 'Publishing',
        className: '',
        actions: customActions,
        // Calendar | Posts | Feed peer navigation
        // (`CalendarTabs`) rides in via bottomComponent.
        bottomComponent,
      };
    }

    if (path.startsWith('/settings')) {
      return { skipRender: true };
    }

    if (path.startsWith('/campaigns')) {
      return {
        // Campaigns has a reporting index plus route-owned detail surfaces
        // (launch history and provider authoring). The INDEX is handled by
        // OBJECT_PAGE_HEADERS above; this branch only serves the children,
        // which set their own explicit customTitle.
        title: customTitle || 'Campaigns',
        className: '',
        actions: customActions,
      };
    }

    // Reach covers people, events, lists, and both LinkedIn and email drafts.
    // The short product noun matches the sidebar and avoids implying that the
    // whole surface is a LinkedIn integration or only an event workflow.
    // `/reach` itself only redirects (UX PRD §7.1/§7.4) and never renders a
    // header of its own. Event detail sets its own `customTitle` once the
    // event loads.
    if (path.startsWith('/reach')) {
      return {
        title: customTitle || 'Reach',
        className: '',
        actions: customActions,
      };
    }

    // Briefs / Studio / Assets / Personas (2026-08-07, flattened 2026-08-15):
    // formerly nested under `/creatives/*`. The old `/creatives/*` redirects
    // (and /media, /documents, /library, /files, /archive) were removed
    // outright 2026-08-23 — dead routes, zero in-app links, pre-launch — so
    // these are simply the flat top-level routes now.
    if (path === '/personas') {
      // Personas is contextual to Brief attachment, not a new sidebar row.
      // It still earns the Briefs ancestor/leaf header as peer routes do.
      return {
        title: customTitle || <RouteBreadcrumb />,
        className: '',
        actions: customActions,
      };
    }

    // Assets — plain string title, no tile, no ancestor. Matches
    // Insights/Discover's own top-level treatment below, and matches what
    // opening an asset already renders (`assetsBreadcrumb()`: "Assets ›
    // <title>" — no ancestor there either).
    if (path === '/library-assets') {
      return { title: 'Assets', className: '', actions: customActions };
    }

    // The Briefs LIST itself (`/briefs` exact) is handled by
    // `OBJECT_PAGE_HEADERS` above — unchanged, still its tile — so this only
    // ever matches a real detail depth (`/briefs/:id`, `.../concepts/:id`,
    // `.../design`).
    if (path.startsWith('/briefs/')) {
      return {
        // A brief detail / concept / design page sets its own dynamic
        // `customTitle` (the brief or concept name) once it loads; the
        // breadcrumb below is what renders before that, or if it never sets
        // one — "Briefs / Briefs" the same way Workflows shows
        // "Projects / Workflows" before its own content is ready.
        title: customTitle || <RouteBreadcrumb />,
        className: '',
        actions: customActions,
      };
    }

    if (path.startsWith('/projects')) {
      const titleContent: React.ReactNode = (
        <RouteBreadcrumb
          hasSwitcher={!!projectIdFromUrl}
          renderTitle={
            projectIdFromUrl ? (node) => <BreadcrumbSwitcher>{node}</BreadcrumbSwitcher> : undefined
          }
        />
      );

      return {
        // The list index is a page title, not a breadcrumb. Only a real
        // project detail earns the ancestor/leaf path and switcher.
        title: projectIdFromUrl ? titleContent : 'Projects',
        noPadding: false,
        className: '',
        // Project detail sets its ⋯ actions here (mobile) via useHeader; the
        // list page never sets them, so it stays null.
        actions: customActions,
      };
    }

    if (path === '/products') {
      // MIGRATED 2026-08-27: the Refresh action now lives in `Products.tsx` and
      // arrives through `setHeaderActions` + `PageHeaderActionLayout`, like the
      // other 11 routes. The manager defines the layout component's contract
      // and had become its largest violator — three route families building
      // their own `actions:` JSX with a hand-rolled `.header-nav-btn`, which is
      // exactly why `/products`, `/discover` and `/insights` had a button
      // ground and `/campaigns` and `/publishing` did not.
      return {
        title: 'Products',
        className: '',
        actions: customActions,
      };
    }

    if (path === '/discover') {
      if (isSignedOut) {
        return {
          title: (
            <Link
              to="/"
              aria-label="Mora home"
              className="flex items-center transition-opacity hover:opacity-80"
            >
              <span className="hidden md:inline-flex">
                <Logo iconClassName="h-[18px]" />
              </span>
              <span className="md:hidden">
                <LogoText />
              </span>
            </Link>
          ),
          className: '',
          actions: (
            <button
              type="button"
              onClick={() => setComingSoonOpen(true)}
              className="text-sm font-medium text-foreground transition-colors hover:text-foreground/80"
            >
              Sign up
            </button>
          ),
        };
      }

      return {
        // Discover / News — the tab strip calls this view News, so the crumb
        // does too. A bare "Discover" collapsed the group and the view into
        // one word.
        title: <RouteBreadcrumb />,
        className: '',
        // ⚠️ RATCHETED VIOLATION, and the guard names it: this `actions:` JSX
        // still lives in the manager rather than in `DiscoverPage.tsx`, because
        // the signed-out branch above owns the same slot and the two would
        // race. The CHROME is fixed regardless — the ground now comes from
        // `PageHeaderActionLayout`, not a hand-rolled `.header-nav-btn`, so a
        // lone Discover icon and a lone Campaigns ⋯ are the same object again.
        actions: (
          <PageHeaderActionLayout
            surface="chrome"
            desktopActions={
              <CircleIconButton
                ariaLabel="Customize Discover"
                tooltip="Customize"
                size="header"
                onClick={() => window.dispatchEvent(new CustomEvent('openDiscoverMenu'))}
              >
                <SlidersHorizontalIcon />
              </CircleIconButton>
            }
          />
        ),
      };
    }

    if (path === '/discover/ads') {
      return { title: <RouteBreadcrumb />, className: '' };
    }

    if (path.startsWith('/discover/')) {
      return {
        title: customTitle,
        actions: customActions,
        bottomComponent,
        className: '',
      };
    }

    if (path === '/ad-copy') {
      return {
        title: <RouteBreadcrumb />,
        className: '',
        actions: customActions,
      };
    }

    // Test page
    if (path === '/test') {
      return {
        title: 'Test Page',
        className: '',
      };
    }

    // Default fallback — render the brand wordmark through the primitive so
    // it inherits canonical Sora typography + tracking instead of leaking as
    // raw text in whatever the page-header font happens to be.
    return {
      title: <LogoText />,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    projectIdFromUrl,
    activeTab,
    navigate,
    customTitle,
    customActions,
    bottomComponent,
    searchParams,
    isMobile,
    isSignedOut,
    isDrawerOpen,
    hasArtifacts,
    isProjectBound,
    isHubOpen,
    canShowProjectRail,
    planId,
    hasShopifyConnected,
  ]);

  const [chatHasMessages, setChatHasMessages] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ hasMessages?: boolean }>).detail;
      setChatHasMessages(!!detail?.hasMessages);
    };
    window.addEventListener('mora:chat-activity', handler);
    return () => window.removeEventListener('mora:chat-activity', handler);
  }, []);

  const promotionalActions = useMemo(
    () =>
      // Jobs/Rams — Upgrade only belongs on the truly empty home greeting.
      // The moment a workflow / project context exists (?planId=...), the
      // user is mid-task and a sales upsell is visual noise. Hide it.
      location.pathname === '/' && !chatHasMessages && !planIdFromUrl && !isTeammate ? (
        <UpgradeHeaderAction onUpgrade={() => navigate('/settings/billing')} />
      ) : null,
    [location.pathname, navigate, chatHasMessages, planIdFromUrl, isTeammate],
  );

  const globalActions = useMemo(
    () => (
      // Avatar removed from the page header (2026-07): like ChatGPT / Claude,
      // the profile lives in the sidebar (its footer user menu), reachable on
      // mobile via ☰. Keeping it out of the header frees the right side for the
      // one page action + ⋯, per the Header Law.
      <>{promotionalActions}</>
    ),
    [promotionalActions],
  );

  const combinedActions = useMemo(
    () => (
      // Audit pass 5 (2026-05-08): tightened to gap-0 (from gap-1 / gap-1.5)
      // because page header action icons have plenty of internal padding.
      // Combined with the size="lg" → default downsize, this makes the
      // right-cluster read as a tight, premium coherent discrete group.
      <div className="flex items-center gap-0">
        {pageConfig.actions}
        {globalActions}
        {(pageConfig as { trailingActions?: React.ReactNode }).trailingActions}
        {/* New-chat "+" — the single gradient accent, rightmost on the home
            header (ChatGPT / Claude model). Shown only once a conversation
            exists, so the empty home keeps its Upgrade CTA and never stacks a
            third right-side button. */}
        {location.pathname === '/' && chatHasMessages ? <NewChatButton /> : null}
      </div>
    ),
    [pageConfig, globalActions, location.pathname, chatHasMessages],
  );

  if ('skipRender' in pageConfig && pageConfig.skipRender) {
    return null;
  }

  // Header Law: a nested view shows a back arrow (returns to the parent),
  // NOT the hamburger. Store-set backHref wins; otherwise derive it from the
  // route so opened projects / discover articles get a ← automatically.
  const routeBackHref = pathProjectId
    ? '/projects'
    : location.pathname.startsWith('/discover/') && location.pathname.split('/').length > 2
      ? '/discover'
      : null;
  const effectiveBackHref = backHref ?? routeBackHref;
  const effectiveBackLabel =
    backLabel ??
    (routeBackHref === '/projects'
      ? 'Projects'
      : routeBackHref === '/discover'
        ? 'Discover'
        : null);

  const configWithGlobalActions = {
    ...pageConfig,
    actions: combinedActions,
    backHref: effectiveBackHref,
    backLabel: effectiveBackLabel,
  };
  const headerContent = pageConfig.showShimmer ? (
    <PageHeaderWithShimmer {...configWithGlobalActions} />
  ) : (
    <PageHeader {...(configWithGlobalActions as any)} />
  );

  return (
    <div className="w-full">
      {headerContent}
      <ComingSoonDialog open={comingSoonOpen} onOpenChange={setComingSoonOpen} />
    </div>
  );
};

/**
 * PageHeader with loading shimmer for dynamic content
 */
const PageHeaderWithShimmer: React.FC<any> = ({ title, subtitle, actions, ...props }) => {
  return <PageHeader title={title} subtitle={subtitle} actions={actions} {...props} />;
};

export default PageHeaderManager;
